import React, { FC, SyntheticEvent, useEffect, useMemo, useState } from "react";

import AutoSelectAsyncList from '@pixel/core/AutoSelectAsyncList'
import useDimensionDropdownData from "../../../hooks/useDimensionDropdownData";
import { transformAppliedAccessGroupFilterObject, transformDimensionDropdownData } from "../OdsTransform";
import { IDepartments, IDimensionOption, IDimensionsDropdownProps } from "../OdsTypes";
import { ListAppliedSearchFilterValueProps } from "@pixel/core";
import ToolTip from "@pixel/core/Tooltip";
import { Chip } from "@mui/material";

const DepartmentsDropdown: FC<IDimensionsDropdownProps> = ({
  searchFilter,
  onChangeAppliedFilter,
  appliedSearchFilter
}) => {
  const [textInput, setTextInput] = useState('');
  const [page, setPage] = useState(0);
  const [departments, setDepartments] = useState<Array<IDimensionOption>>([]);
  const [appliedFilterState, setAppliedFilterState] = useState(appliedSearchFilter);
  const [appliedFiltersUpdated, setAppliedFiltersUpdated] = useState(false);
  const appliedFilterValues = useMemo(() => {
    return appliedFilterState && appliedFilterState?.filterValues.map((filterValue) => filterValue) || [];
  }, [appliedFilterState]);
  const [appliedFilterValuesState, setAppliedFilterValuesState] = useState(appliedFilterValues);
  const appliedFilterValuesOptions = useMemo(() => {
    if (appliedFilterValuesState) {
      return appliedFilterValuesState.map((filterValue) => ({
        value: filterValue.value,
        label: filterValue.label,
      }));
    } else {
      return [];
    }
  }, [appliedFilterValuesState]);
  const [options, setOptions] = useState(appliedFilterValuesOptions);

  useEffect(() => {
    if (appliedFilterState !== appliedSearchFilter) {
      setAppliedFilterState(appliedSearchFilter);
      setAppliedFiltersUpdated(true);
    }
  }, [appliedSearchFilter]);

  useEffect(() => {
    if (appliedFilterValuesState !== appliedFilterValues) {
      setAppliedFilterValuesState(appliedFilterValues);
    }
  }, [appliedFilterValues]);

  useEffect(() => {
    if (appliedFilterValuesOptions !== options) {
      setOptions(appliedFilterValuesOptions);
    }
  }, [appliedFilterValuesOptions]);

  useEffect(() => {
    if (!appliedFiltersUpdated) {
      handleChangeAppliedFilterAll();
    } else {
      setAppliedFiltersUpdated(false);
    }
  }, [options]);

  const handleChangeSelect = (_event: SyntheticEvent<Element, Event>, menuItemSelect: Array<ListAppliedSearchFilterValueProps>) => {
    setAppliedFilterValuesState(menuItemSelect);
    setTextInput('');
  }

  const handleChangeAppliedFilterAll = () => {
    if (options?.length >= 1) {
      const appliedFiltersObject = transformAppliedAccessGroupFilterObject(searchFilter, options);
      onChangeAppliedFilter(searchFilter, appliedFiltersObject);
    } else {
      onChangeAppliedFilter(searchFilter, null);
    }
  }

  const {
    loading,
  } = useDimensionDropdownData({
    page,
    url: '/action/ods/departments/dropdown',
    keywords: textInput,
    onNewResults: (response: IDepartments) => {
      setDepartments(transformDimensionDropdownData(response?.departments));
    },
    onNextPageResults: (response: IDepartments) => {
      const departments = transformDimensionDropdownData(response?.departments);
      setDepartments(currentData => [...currentData, ...departments]);
    }
  });

  const renderCustomOptionChip = (option: IDimensionOption, index: number, getTagProps: Function) => {
    const label = option?.label;
    if (label?.length > 10) {
      const truncatedLabel = label?.length > 10 ? `${label?.substring(0, 10)}...` : label;
      return (
        <ToolTip title={label} key={option.value}>
          <Chip label={truncatedLabel} {...getTagProps({ index })} />
        </ToolTip>
      );
    } else {
      return <Chip label={label} key={option.value}  {...getTagProps({ index })} />
    }
  }

  return (
    <AutoSelectAsyncList
      multiple
      id={searchFilter.id}
      placeholder={searchFilter.placeholder}
      debounceInput={100}
      loading={loading}
      loadingNextPage={loading}
      noResults={departments.length === 0}
      options={departments}
      inputValue={textInput}
      onDebouncedKeyStroke={(newInput: string) => {
        setPage(0)
        setTextInput(newInput)
      }}
      onFetchNextPage={() => setPage(current => current + 1)}
      currentPage={page}
      disableCloseOnSelect={true}
      renderOption={(props, option: IDimensionOption) => (
        <div style={{ display: 'flex', alignItems: 'center' }} {...props}>
          <div style={{ padding: '5px 0' }}>
            <span style={{ color: '#1872cc', display: 'block', lineHeight: '18px' }}>{option.label}</span>
          </div>
        </div>
      )}
      getOptionLabel={(option: IDimensionOption) => option.label}
      getValueLabel={(option: IDimensionOption) => option.label}
      isOptionEqualToValue={(option: IDimensionOption, value: IDimensionOption) => option.value === value.value}
      onChange={handleChangeSelect}
      value={options}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option: IDimensionOption, index: number) =>
          renderCustomOptionChip(option, index, getTagProps)
        )
      }
    />
  )
}

export default DepartmentsDropdown;